<template>
  <div
    class="w-full border border-gray-300 dark_border-gray-700 rounded-md p-3 text-sm dark_text-gray-200"
    :class="{
      'bg-green-500/20': state === 'accepted',
      'bg-red-500/20': state === 'rejected',
      'bg-sky-500/20': state === 'pending'
    }"
  >
    <header class="mb-2 flex items-start justify-between gap-2">
      <h4 class="m-0 text-sm text-gray-700 dark_text-gray-200">
        <span class="font-bold">
          {{ statusEmoji[state] }} Text edit
          {{ index != null ? `#${index + 1}` : '' }}
        </span>
        <span v-if="screenTitle" class="font-normal">
          &ndash; {{ screenTitle }}</span
        >
        <span
          v-if="!isValid"
          class="bg-red-100 dark_bg-red-900 text-red-700 dark_text-red-200 px-1.5 py-0.5 rounded text-xs ml-2"
          >Invalid</span
        >
      </h4>

      <button
        @click="isCollapsed = !isCollapsed"
        class="p-1 rounded bg-gray-500/0 hover_bg-gray-200 dark_hover_bg-gray-700 transition-all flex-shrink-0"
        :title="isCollapsed ? 'Expand diff' : 'Collapse diff'"
      >
        <Icon
          icon="ph:caret-down"
          class="w-4 h-4 text-gray-600 dark_text-gray-400 transition-transform"
          :class="{ 'rotate-180': !isCollapsed }"
        />
      </button>
    </header>

    <!-- Error state -->
    <div v-if="!isValid && !isCollapsed">
      <p class="text-red-600 dark_text-red-300 text-sm font-mono">
        {{ errorMessage }}
      </p>
    </div>

    <!-- Diff display -->
    <div v-else-if="!isCollapsed" class="space-y-1 text-xs font-mono">
      <div
        class="bg-red-50 dark_bg-red-900/30 rounded px-2 py-1 whitespace-pre-wrap text-red-800 dark_text-red-300 line-through opacity-80"
      >
        {{ parsed?.oldText }}
      </div>
      <div
        class="bg-green-50 dark_bg-green-900/30 rounded px-2 py-1 whitespace-pre-wrap text-green-800 dark_text-green-300"
      >
        {{ parsed?.newText }}
      </div>
    </div>

    <!-- Actions -->
    <footer class="mt-3 flex justify-end gap-2">
      <label
        v-if="canView && !isFinal"
        :for="`preview-${chunkKey}`"
        class="flex items-center gap-2 me-auto select-none text-gray-700 dark_text-gray-200"
      >
        <input
          :id="`preview-${chunkKey}`"
          type="checkbox"
          v-model="previewChecked"
          :disabled="!canApply"
          title="Apply this change temporarily in the preview"
        />
        <span>Preview changes</span>
      </label>
      <button
        v-if="canView"
        class="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-gray-700 hover_bg-gray-300 dark_bg-gray-700 dark_text-gray-200 dark_hover_bg-gray-600 transition-colors"
        @click="handleView"
        title="View this screen in the preview"
      >
        View
      </button>

      <template v-if="state === 'accepted'">
        <button
          class="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-gray-700 hover_bg-gray-300 dark_bg-gray-700 dark_text-gray-200 dark_hover_bg-gray-600 transition-colors"
          @click="screenId && $emit('revert', screenId)"
        >
          Revert
        </button>
      </template>
      <template v-else-if="state === 'rejected'">
        <button
          class="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-gray-700 hover_bg-gray-300 dark_bg-gray-700 dark_text-gray-200 dark_hover_bg-gray-600 transition-colors"
          @click="$emit('un-reject')"
        >
          Undo Reject
        </button>
      </template>
      <template v-else>
        <button
          class="px-3 py-1.5 rounded text-sm font-medium bg-gray-200 text-gray-700 hover_bg-gray-300 dark_bg-gray-700 dark_text-gray-200 dark_hover_bg-gray-600 transition-colors"
          @click="$emit('reject')"
        >
          Reject
        </button>
        <button
          class="px-3 py-1.5 rounded text-sm font-medium bg-blue-500 dark_bg-blue-600 text-white hover_bg-blue-600 dark_hover_bg-blue-500 transition-colors disabled_opacity-50 disabled_cursor-not-allowed"
          :disabled="!isValid || !canApply"
          @click="emitAccept"
          :title="!canApply ? conflictMessage : undefined"
        >
          Accept
        </button>
      </template>
    </footer>

    <!-- Conflict warning -->
    <p
      v-if="conflictMessage && state === 'pending'"
      class="mt-2 text-xs text-amber-600 dark_text-amber-400"
    >
      ⚠ {{ conflictMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue';
import { load as yamlLoad } from 'js-yaml';
import applyTextEdit from '../../edit-agent/lib/applyTextEdit';
import { findScreenById } from '../../lib/applyPatch';
import getPageIdForScreen from '../lib/getPageIdForScreen';
import { registerPreview, unregisterPreview } from '../lib/useActivePreview';

type PatchState = 'accepted' | 'rejected' | 'pending';

type TextEditPayload = {
  screenId: string;
  oldText: string;
  newText: string;
};

const props = withDefaults(
  defineProps<{
    /** Raw JSON string from inside <text-edit>...</text-edit> */
    content: string;
    /** Full compiled YAML of current draft */
    currentYaml: string;
    index?: number;
    state?: PatchState;
    /** Globally unique key identifying this chunk */
    chunkKey: string;
    /** Ordering across all chunks in the conversation, for stable composition */
    order: number;
  }>(),
  {
    state: 'pending'
  }
);

const emit = defineEmits<{
  /** Emitted with full reconstituted screen YAML — same contract as PatchPreview */
  (e: 'accept', screenYaml: string): void;
  (e: 'reject'): void;
  (e: 'revert', screenId: string): void;
  (e: 'un-reject'): void;
  /** Emitted when author clicks View - contains pageId and screenId */
  (e: 'view', pageId: string, screenId: string): void;
}>();

const isCollapsed = ref(props.state !== 'pending');
const isValid = ref(true);
const errorMessage = ref('');
const parsed = ref<TextEditPayload | null>(null);
const conflictMessage = ref('');

const previewChecked = ref(true);
const isPreviewed = ref(false);
const lastPreviewedId = ref<string | null>(null);

const isFinal = computed(
  () => props.state === 'accepted' || props.state === 'rejected'
);

watch(
  () => props.state,
  newState => {
    isCollapsed.value = newState !== 'pending';
  }
);

// Parse the raw JSON content
const validate = () => {
  try {
    const p = JSON.parse(props.content.trim()) as TextEditPayload;
    if (
      !p.screenId ||
      typeof p.oldText !== 'string' ||
      typeof p.newText !== 'string'
    ) {
      throw new Error('Missing screenId, oldText, or newText');
    }
    parsed.value = p;
    isValid.value = true;
    errorMessage.value = '';
  } catch (err: any) {
    isValid.value = false;
    errorMessage.value = err.message || 'Could not parse text edit';
    parsed.value = null;
  }
};

watch(() => props.content, validate, { immediate: true });

const screenId = computed(() => parsed.value?.screenId ?? null);

const canView = computed(() => screenId.value !== null);

const handleView = () => {
  if (!screenId.value) return;
  try {
    const currentObj = yamlLoad(props.currentYaml) as any;
    const pageId = getPageIdForScreen(screenId.value, currentObj);
    if (pageId) emit('view', pageId, screenId.value);
  } catch {
    // ignore
  }
};

/** Screen heading from the current course for display purposes */
const screenTitle = computed<string>(() => {
  if (!screenId.value) return '';
  try {
    const obj = yamlLoad(props.currentYaml) as any;
    const screen = findScreenById(obj, screenId.value);
    if (!screen) return screenId.value;
    return (
      (screen as any).heading ||
      (screen as any).title ||
      (screen as any).subheading ||
      ''
    );
  } catch {
    return screenId.value;
  }
});

/**
 * Check whether the oldText is currently present in the screen.
 * Recomputed whenever currentYaml or the parsed payload changes.
 */
const canApply = computed<boolean>(() => {
  if (!isValid.value || !parsed.value) return false;
  const result = applyTextEdit(
    props.currentYaml,
    parsed.value.screenId,
    parsed.value.oldText,
    parsed.value.newText
  );
  if (!result.found) {
    conflictMessage.value =
      'The original text was not found in this screen — the course may have changed. Try refreshing and asking again.';
    return false;
  }
  conflictMessage.value = '';
  return true;
});

const previewYaml = computed<string | null>(() => {
  if (!isValid.value || !parsed.value) return null;
  const result = applyTextEdit(
    props.currentYaml,
    parsed.value.screenId,
    parsed.value.oldText,
    parsed.value.newText
  );
  return result.found ? result.screenYaml : null;
});

function emitAccept() {
  if (!previewYaml.value) {
    conflictMessage.value =
      'The original text was not found in this screen — the course may have changed.';
    return;
  }
  emit('accept', previewYaml.value);
}

watch(
  [
    previewChecked,
    isValid,
    screenId,
    canApply,
    () => props.state,
    () => parsed.value
  ],
  ([checked, valid, id, canApplyVal, state]) => {
    const active =
      state !== 'rejected' &&
      state !== 'accepted' &&
      checked &&
      valid &&
      canApplyVal &&
      !!id &&
      !!parsed.value;
    if (!active) {
      unregisterPreview(props.chunkKey);
      isPreviewed.value = false;
      lastPreviewedId.value = null;
      return;
    }

    registerPreview(props.chunkKey, {
      screenId: id as string,
      order: props.order,
      edit: {
        type: 'text-edit',
        oldText: parsed.value!.oldText,
        newText: parsed.value!.newText
      }
    });
    isPreviewed.value = true;
    lastPreviewedId.value = id as string;
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  unregisterPreview(props.chunkKey);
});

const statusEmoji = {
  accepted: '✅',
  rejected: '❌',
  pending: '⏳'
};
</script>
